import React, {useState} from "react";
import {Spinner} from "components/common/Spinner";
import {
  CumulativeData,
  DateEx,
  DateHelper,
  LinePoints,
  SignalRepeatType,
  SignalsData,
  SignalsMap
} from "predictagram-lib";
import {Form, Formik} from "formik";
import ReactDatePicker from "react-datepicker";
import {SymbolsDropdown} from "../../../common/SymbolsDropdown";
import {adminPredictionApiService} from "../../../../services/AdminApiService";
import {StockHelper} from "../../../../_utils/stock.helper";
import {ChartModel as ChartOptions} from "../../../../models/chart.model";
import {DropDown} from "../../../common/form/DropDown";
import {ExportToCsv} from "export-to-csv-fix-source-map";
import {AnalysisFilters} from "../../../common/analysis/analysis.filters";

type FilterOptions = {startTime:number, endTime:number, symbolName: string, signalType: string, repeatSecs: number};
const Filter: React.FunctionComponent<{
  initialValues?: Partial<FilterOptions>,
  onClick: (search:any)=>void,
}> = ({ initialValues, onClick }) => {

  const signals = new Map(SignalsMap);
  const stockDay = StockHelper.findTradingDay(new Date(), -1, false);
  const todayStart = stockDay.openAt();
  const todayEnd = stockDay.closeAt();
  const repeatIntervalSecs = AnalysisFilters.SignalRepeatTypes;

  const defVals: FilterOptions = {
    startTime: todayStart,
    endTime: todayEnd,
    symbolName: 'SPY',
    signalType: signals.keys().next().value,
    repeatSecs: repeatIntervalSecs.keys().next().value,
  };
  const initVals: FilterOptions = Object.assign(defVals, initialValues) as FilterOptions;

  return (
    <div>
      <Formik initialValues={initVals} onSubmit={onClick}>
        {({ values, touched, errors, setFieldValue }) => {
          return <Form>
            <div className="d-flex justify-content-start align-items-center gap-3 my-3">
              <div className="d-flex flex-column gap-2">
                <SymbolsDropdown name="symbolName" errors={errors} touched={touched} />
              </div>
              <DropDown enumMap={signals}
                  errors={errors}
                  touched={touched}
                  label="Signal" name={'signalType'}
              />
              <DropDown enumMap={repeatIntervalSecs}
                        errors={errors}
                        touched={touched}
                        label="Repeat" name={'repeatSecs'}
              />
              <div className="d-flex flex-column">
                <span className="fw-bold">Start Date</span>
                <ReactDatePicker
                  dateFormat="MM/dd/yyyy"
                  onChange={(date: Date) => {
                    setFieldValue("startTime", date.getTime() / 1000);
                  }}
                  selected={new Date(values.startTime * 1000)}
                />
              </div>

              <div className="d-flex flex-column">
                <span className="fw-bold">End Date</span>
                <ReactDatePicker
                  dateFormat="MM/dd/yyyy"
                  onChange={(date: Date) => {
                    setFieldValue("endTime", date.getTime() / 1000);
                  }}
                  selected={new Date(values.endTime * 1000)}
                />
              </div>
              <div className="align-self-end">
                <button type="submit" className="btn btn-primary p-2">Generate Report</button>
              </div>
            </div>

          </Form>
        }}

      </Formik>
    </div>
  )
}


export const AdminReportSignals: React.FunctionComponent = () => {


  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('');

  const generateReportWrap = async(searchOptions:FilterOptions) => {
    try {
      await generateReport(searchOptions);
    } catch(e) {
      console.error(e);
    }
  }

  const generateReport = async(searchOptions:FilterOptions) => {
    setStatus('');
    setIsLoading(true);

    type RepItem = {
      signalDateTimeEst: string;
      createdAtEpoch: number;
      categories: string;
      priceAtPrediction: number;
      sidePrediction: number;
      highPrice: number;
      highPriceDateTimeEst: string;
      highPriceEpochSecs: number;
      lowPrice: number;
      lowPriceDateTimeEst: string;
      lowPriceEpochSecs: number;
      plus30MinsPrice: number;
      plus30MinsDateTimeEst: string;
      plus30MinsEpochSecs: number;
      dayClosePrice: number;
      dayClosePriceDateTimeEst: string;
      dayClosePriceEpochSecs: number;
    };
    const rep: RepItem[] = [];
    const startTimeInit = searchOptions.startTime;
    const endTimeInit = searchOptions.endTime;
    const symbol = searchOptions.symbolName;
    const signalType = searchOptions.signalType;
    const repeatSecs = searchOptions.repeatSecs;

    let currDay = StockHelper.findTradingDay(new DateEx(startTimeInit * 1000), -1, true);
    while(currDay.openAt()<endTimeInit) {
      const startD  = new DateEx(currDay.openAt()*1000);
      const endD = new DateEx(currDay.closeAt()*1000);
      const stats= await adminPredictionApiService.getBarsData(symbol, startD, endD);
      const items = await adminPredictionApiService.cumulativeData({symbolNames:[symbol], startTimePredMade: startD.getTimeSec(), endTimePredMade: endD.getTimeSec()});
      const allStats = { predStats: items.sort((a, b) => a.valueTime - b.valueTime), stockStats: stats };
      // @TODO: can be refactored
      const d = ChartOptions.prebuildChartDay({
        stats: allStats.stockStats,
        endFillTimeSec: endD.getTimeSec(),
        chartWidth: 400
      });
      const stockLine = {x: d.plotLines.during.x, y: d.plotLines.during.y} as LinePoints;
      // @TODO: the page can be just removed later
      const cumulData = CumulativeData.calcCumulativeChartData(allStats, stockLine, {
        prevCloseThreshold:0, prevClose: 0,
      });

      const signals = SignalsData.signalsCalcAdjust((cumulData as any)[signalType] as any, {repeatSecs, endTimeSecs: endD.getTimeSec()});
      for (const s of signals) {
        const sTime = s.x;
        let high = null as any;
        let highTime = null as any;

        let low = null as any;
        let lowTime = null as any;

        let signalPrice = null as any;

        let plus30MinsPrice = null as any;
        let plus30MinsTime = null as any;
        let dayClosePrice = null as any;
        let dayCloseTime = null as any;

        for (const stat of stats) {
          if (stat.t===sTime) {
            signalPrice = stat.c;
          }
          if (stat.t>=sTime) {
            if (high===null || stat.c>high) {
              high = stat.c;
              highTime = stat.t;
            }
            if (low===null || stat.c<low) {
              low = stat.c;
              lowTime = stat.t;
            }
          }
          if (stat.t === sTime + (60 * 30)) {
            plus30MinsPrice = stat.c;
            plus30MinsTime = stat.t;
          }

          if (stat.t === (currDay.closeAt() - 59)) { // stats dont have seconds, so remove :59 from 3:59:59
            dayClosePrice = stat.c;
            dayCloseTime = currDay.closeAt() - 59;
          }
        }
        rep.push({
          [`${signalType}DateTimeEst`]: DateHelper.dateTimeFormatUs(sTime),
          createdAtEpoch: sTime,
          categories: s.repeatType===SignalRepeatType.PRIMARY ? 'Primary' : 'Repeat',
          priceAtPrediction: signalPrice,
          sidePrediction: s.isSellSignal ? -1 : 1,
          highPrice: high,
          highPriceDateTimeEst: DateHelper.dateTimeFormatUs(highTime),
          highPriceEpochSecs: highTime,
          lowPrice: low,
          lowPriceDateTimeEst: DateHelper.dateTimeFormatUs(lowTime),
          lowPriceEpochSecs: lowTime,
          plus30MinsPrice,
          plus30MinsDateTimeEst: plus30MinsTime ? DateHelper.dateTimeFormatUs(plus30MinsTime) : '',
          plus30MinsEpochSecs: plus30MinsTime,
          dayClosePrice,
          dayClosePriceDateTimeEst: dayCloseTime ? DateHelper.dateTimeFormatUs(dayCloseTime) : '',
          dayClosePriceEpochSecs: dayCloseTime,
        } as RepItem);
      }

      currDay = currDay.nextDay();
    }
    const dateStr = (time:number)=>DateHelper.dateTimeFormatUs(time).substring(0, 10);
    const filename = `signals_data_${symbol}-${dateStr(startTimeInit)}-${dateStr(endTimeInit)}`;
    if (rep.length === 0) {
      setStatus('no data found for specified range');
    } else {
      const csv = new ExportToCsv({ useKeysAsHeaders: true, quoteStrings: '', filename});
      csv.generateCsv(rep);
    }

    setIsLoading(false);
  }

  return (
    <>
    <div>
      <div className="d-flex justify-content-center align-items-end gap-3">
      <Filter onClick={generateReportWrap}/>
      </div>
    </div>

    {isLoading && <Spinner />}

    {status && <div>{status}</div>}
    </>
  );
};


